/**
 * Bytecode format and encoding for loopmaster
 * All data is encoded as numbers for SharedArrayBuffer storage
 */

import { OpCode } from '../as/assembly/bytecode.ts'
export { DelayType, FilterType, LfoType, OpCode, OscType } from '../as/assembly/bytecode.ts'

// Audio constants - these should match constants.ts
export const SAMPLE_RATE = 48000
export const MAX_SAMPLE_TIME = 15 // seconds
export const MAX_SAMPLE_LENGTH = MAX_SAMPLE_TIME * SAMPLE_RATE // 720000 samples

// Waveform visualization constants - must match as/assembly/constants.ts
export { WAVEFORM_BUFFER_SIZE } from '../as/assembly/constants.ts'

// Simple string hash for IDs (deterministic)
export function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return hash
}

export function hashAudioBuffer(audioBuffer: AudioBuffer): number {
  const channelData = audioBuffer.getChannelData(0)
  return hashFloat32Array(channelData)
}

export function hashFloat32Array(data: Float32Array): number {
  const length = data.length

  if (length === 0) {
    return 1 // Use 1 for empty buffers to avoid 0
  }

  // Use a seed to ensure we don't get 0
  let hash = 5381 // DJB2 hash seed

  // Sample at regular intervals to compute hash efficiently
  // For very long buffers, sample every N samples
  const step = length > 100000 ? Math.floor(length / 10000) : 1
  const samplesToHash = Math.min(length, 10000)

  for (let i = 0; i < samplesToHash; i++) {
    const idx = i * step
    if (idx >= length) break
    const sample = data[idx]
    // Convert float to int for hashing (multiply by large number and truncate)
    // Use absolute value and add offset to ensure positive values
    const sampleInt = Math.floor(Math.abs(sample) * 1000000) + 1
    hash = ((hash << 5) + hash) + sampleInt
    // Convert to 32-bit signed integer (bitwise OR with 0 forces 32-bit conversion)
    hash = hash | 0
  }

  // Also include length in hash
  hash = ((hash << 5) + hash) + length
  // Convert to 32-bit signed integer
  hash = hash | 0

  // Use Math.abs to ensure positive, then add 1 to ensure it's never 0
  hash = Math.abs(hash)
  if (hash === 0) {
    hash = 1
  }

  // Final conversion to ensure 32-bit signed integer
  return hash | 0
}

// Bytecode is a flat array of Float64 values
// Each instruction has: [opcode, ...args]
// Variable length instructions, decoder must know arg count per opcode

export interface NumberWithParamsInfo {
  value: number
  line: number
  column: number
  length: number
  min: number
  max: number
}

export interface PatternEventInfo {
  line: number
  column: number
  length: number
  type: 'note' | 'rest' | 'chord'
  sequenceId?: number // Sequence ID this event maps to
  noteIndex?: number // Note index within the sequence
}

export interface AmplitudeInfo {
  id: number // Unique waveform ID
  line: number
  column: number
  length: number
}

export interface WaveformInfo {
  id: number // Unique waveform ID
  line: number
  column: number
  length: number
}

export interface LevelInfo {
  id: number // Unique waveform ID
  line: number
  column: number
  length: number
}

export interface TimelineInfo {
  id: number // Unique timeline ID
  line: number
  column: number
  length: number
  pattern: string
  beat: number
  segmentIndex?: number // Parsed segment index (matches TimelinePosition.segmentIndex)
  characterIndex?: number // Character index in pattern string
  segmentType?: 'rise' | 'fall' | 'hold'
  segmentValue?: number
  segmentDivisions?: number
}

export interface BytecodeProgram {
  bytecode: Float64Array
  sequences: SequenceData[]
  bpm: number
  tune: number
  hasExplicitBpm: boolean // True if program explicitly set BPM (should not be overridden by global BPM)
  impulseResponses: string[]
  samples: string[]
  sampleIdMap: Map<string, number> // Not used anymore, kept for compatibility
  samplePaths: string[] // Array of paths in order of ID assignment (index = ID)
  samSpeeches: [number, any][]
  numbersWithParams: NumberWithParamsInfo[]
  patternEvents: PatternEventInfo[]
  amplitudeInfos: AmplitudeInfo[]
  waveformInfos: WaveformInfo[]
  levelInfos: LevelInfo[]
  timelineInfos: TimelineInfo[]
}

export interface SequenceData {
  id: number // hashed
  notes: NoteData[]
  swing?: number // swing amount (0-1)
  offset?: number // offset in seconds
  exponent?: number // curve exponent for slide interpolation
  cycleLength?: number // original cycle length in notes (for nested alternating patterns)
}

export interface NoteData {
  pitches: Float32Array
  duration: number
  velocity: number
  hold: number
  glide: number
}

/**
 * Encodes a bytecode program into SharedArrayBuffers
 */
export class BytecodeEncoder {
  private bytecode: number[] = []

  emit(opcode: OpCode, ...args: number[]): void {
    this.bytecode.push(opcode)
    this.bytecode.push(args.length)
    this.bytecode.push(...args)
  }

  getBytecode(): Float64Array {
    return new Float64Array(this.bytecode)
  }

  reset(): void {
    this.bytecode = []
  }
}

/**
 * SharedArrayBuffer layout:
 *
 * Control Buffer (Int32Array):
 * [0] = active program buffer index (0-3)
 * [1] = is playing (0 or 1)
 * [2] = bpm (as int, divide by 100 to get float)
 * [3] = program ready flag (0 or 1) - set to 1 when new program is ready
 * [4] = sequence count for current program
 * [5] = bytecode length for program buffer 0
 * [6] = bytecode length for program buffer 1
 * [7] = bytecode length for program buffer 2
 * [8] = bytecode length for program buffer 3
 *
 * Program Buffers (Float64Array[]):
 * Array of bytecode instructions
 *
 * Sequence Buffers (Float64Array[]):
 * Per-sequence: [noteCount, note0_pitchCount, note0_pitch0, ..., note0_duration, note0_velocity, ...]
 */

export const CONTROL_BUFFER_SIZE = 128 // Int32s (includes space for sequence lengths)
export const PROGRAM_BUFFER_SIZE = 1024 * 16 // Float64s (128KB each)
export const SEQUENCE_BUFFER_SIZE = 1024 * 16 // Float64s (128KB each)
export const NUM_PROGRAM_BUFFERS = 4 // Ring buffer for hot reloading
export const RING_BUFFER_SIZE = 16384 // Float32 samples per channel for time-domain visualization

export interface SharedBuffers {
  control: SharedArrayBuffer
  programs: SharedArrayBuffer[]
  sequences: SharedArrayBuffer[]
  workletProgram: SharedArrayBuffer // For transferring program data to worklet
  workletProgramA: SharedArrayBuffer // For channel A program data
  workletProgramB: SharedArrayBuffer // For channel B program data
  workletSequences: SharedArrayBuffer[] // For transferring sequence data to worklet (first 32 for A, next 32 for B)
  ringBufferLeft: SharedArrayBuffer // Ring buffer for left channel audio output
  ringBufferRight: SharedArrayBuffer // Ring buffer for right channel audio output
  ringBufferControl: SharedArrayBuffer // Write pointer for ring buffer (Int32)
  waveformWritePositions: SharedArrayBuffer // Write positions for waveform buffers (MAX_WAVEFORMS * Int32)
}

export function createSharedBuffers(): SharedBuffers {
  return {
    control: new SharedArrayBuffer(CONTROL_BUFFER_SIZE * Int32Array.BYTES_PER_ELEMENT),
    programs: Array.from(
      { length: NUM_PROGRAM_BUFFERS },
      () => new SharedArrayBuffer(PROGRAM_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    ),
    sequences: Array.from(
      { length: 64 },
      () => new SharedArrayBuffer(SEQUENCE_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    ),
    workletProgram: new SharedArrayBuffer(PROGRAM_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    workletProgramA: new SharedArrayBuffer(PROGRAM_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    workletProgramB: new SharedArrayBuffer(PROGRAM_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    workletSequences: Array.from(
      { length: 64 },
      () => new SharedArrayBuffer(SEQUENCE_BUFFER_SIZE * Float64Array.BYTES_PER_ELEMENT),
    ),
    ringBufferLeft: new SharedArrayBuffer(RING_BUFFER_SIZE * Float32Array.BYTES_PER_ELEMENT),
    ringBufferRight: new SharedArrayBuffer(RING_BUFFER_SIZE * Float32Array.BYTES_PER_ELEMENT),
    ringBufferControl: new SharedArrayBuffer(Int32Array.BYTES_PER_ELEMENT),
    waveformWritePositions: new SharedArrayBuffer(16 * Int32Array.BYTES_PER_ELEMENT), // MAX_WAVEFORMS = 16
  }
}

export const enum ControlIndex {
  ACTIVE_PROGRAM,
  IS_PLAYING,
  BPM,
  PROGRAM_READY, // Flag: 1 = new program ready to load, 0 = loaded
  SEQUENCE_COUNT, // Number of sequences in current program
  WORKLET_PROGRAM_LENGTH, // Length of program data in workletProgram buffer
  WORKLET_SEQUENCE_COUNT, // Number of sequences in workletSequences buffers
  WORKLET_LOAD_READY, // Flag: 1 = data ready for worklet to load into WASM
  WORKLET_LOAD_DONE, // Flag: 1 = worklet finished loading
  SYNC_MODE, // 0 = now, 1 = beat (on-the-beat sync)
  CROSSFADE, // Crossfade value (0-100, representing 0.0-1.0)
  GLOBAL_BPM, // Global BPM override (multiplied by 100)
  TUNE, // Tune multiplier (multiplied by 1000, so 1000 = 1.0, 2000 = 2.0, 500 = 0.5)
  DUAL_CHANNEL_MODE, // Flag: 1 = dual channel mode, 0 = single channel
  WORKLET_PROGRAMA_LENGTH, // Length of program A data
  WORKLET_PROGRAMB_LENGTH, // Length of program B data
  WORKLET_SEQUENCE_COUNT_A, // Number of sequences for channel A
  WORKLET_SEQUENCE_COUNT_B, // Number of sequences for channel B
  OVERSAMPLING, // Oversampling factor: 1 = x1, 2 = x2, 4 = x4, 8 = x8
  BPM_LOCK_SINGLE, // Flag: 1 = BPM is locked (program has explicit bpm), 0 = use global BPM
  WORKLET_SEQ_LENGTHS, // Start of array of sequence lengths (64 slots: 32 for A, 32 for B)
  GLOBAL_SAMPLE_COUNTER, // Global sample counter (updated by worklet, read by main thread)
}
